<template>
  <div
    :id="id"
    class="collapse"
    :class="{ expanded: isExpand }"
  >
    <div
      class="title-container"
      :class="[selfClass, titleContainerClass]"
      @click="handleBarClick"
    >
      <div class="title-content">
        <span
          v-if="title"
          class="default-title"
          @click="titleFunc"
        >
          {{ title }}
        </span>
        <slot
          v-else
          name="title"
        />
      </div>
      <i
        :class="['arrow', isExpand ? expandIcons[0] : expandIcons[1], arrowClass]"
        @click="handleArrowClick"
      />
    </div>
    <slot name="additional" />
    <transition
      :name="transitionName"
      tag="div"
    >
      <template v-if="keepContentAlive">
        <div
          v-show="isExpand"
          key="content"
          class="content"
          :class="{'with-border': withBorder}"
        >
          <slot name="content" />
        </div>
      </template>
      <template v-else>
        <div
          v-if="isExpand"
          key="content"
          class="content"
          :class="{'with-border': withBorder}"
        >
          <slot name="content" />
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue-demi'
import keyMirror from 'keymirror'

const emitEvents = keyMirror({
  'expand-status': null,
})

export default {
  name: 'HKLCollapse',
  props: {
    id: {
      type: String,
      default: 'collapse',
    },
    title: {
      type: String,
      default: '',
    },
    titleFunc: {
      type: Function,
      default: () => {},
    },
    expand: {
      type: Boolean,
      default: true,
    },
    keepOpen: {
      type: Boolean,
      default: false,
    },
    keepContentAlive: {
      type: Boolean,
      default: false,
    },
    withBorder: {
      type: Boolean,
      default: true,
    },
    enableExpandByClickingTitle: {
      type: Boolean,
      default: false,
    },
    titleContainerClass: {
      type: Object,
      default: () => {},
    },
    expandIcons: {
      type: Array,
      default: () => ['fa-angle-up', 'fa-angle-down'],
    },
    enableTransition: {
      type: Boolean,
      default: true,
    },
    arrowStyle: {
      type: String,
      validator: (value) => {
        return ['accordion', 'none'].includes(value)
      },
      default: 'none',
    },
  },
  setup (props, { emit }) {
    onMounted(() => {
      emit(emitEvents['expand-status'], props.id, props.expand)
    })
    return {
      isExpand: props.expand,
      transitionName: computed(() => {
        return props.enableTransition ? 'collapse' : ''
      }),
      arrowClass: computed(() => {
        return props.arrowStyle === 'accordion' ? 'accordion-arrow' : 'fa angle'
      }),
    }
  },
  computed: {
    selfClass () {
      return {'with-border': this.withBorder, 'mouse-pointer': this.enableExpandByClickingTitle}
    },
  },
  watch: {
    expand () {
      if (!this.keepOpen) {
        this.isExpand = this.expand
      }
    },
  },
  methods: {
    collapseAndExpand (isExpand) {
      if (typeof isExpand === 'boolean') {
        this.isExpand = isExpand
      } else {
        this.isExpand = !this.isExpand
      }
      this.$emit(emitEvents['expand-status'], this.id, this.isExpand)
    },
    handleArrowClick () {
      !this.enableExpandByClickingTitle && this.collapseAndExpand()
    },
    handleBarClick () {
      this.enableExpandByClickingTitle && this.collapseAndExpand()
    },
  },
}
</script>

<style lang='scss' scoped>
  .collapse-enter-active {
    animation: easeIn .5s 0s;
    overflow: hidden;
  }
  .collapse-leave-active {
    animation: easeIn .5s 0s reverse;
    overflow: hidden;
  }
  .title-container {
    display: flex;
    align-items: center;
    .default-title {
      color: #0c3a58;
      font-size: 16px;
      font-weight: bold;
      text-transform: capitalize;
    }
    .title-content {
      flex: 1;
      margin-right: 5px;
    }

    .arrow {
      cursor: pointer;
      height: 50px;
      line-height: 50px;
      font-size: 40px;
    }
  }
  .collapse {
    &.title-font14 {
      .default-title {
        font-size: 14px;
      }
    }
    &.overflow-hide-content .content {
      overflow: hidden;
    }
    &.title-responsive {
      .title-container .title-content{
        flex: unset;
      }
    }
    .arrow {
      &.accordion-arrow {
        position: relative;
      }
      &.accordion-arrow::before {
        content: '\2039';
        font-style: normal;
        font-size: 50px;
        color: #27698D;
        font-family: Verdana;
        transform: rotate(90deg);
        position: absolute;
        right: 20px;
      }
      &.accordion-arrow.fa-angle-down::before {
        transform: rotate(-90deg);
        right: 30px;
      }
    }
    &.small {
      .arrow {
        height: 30px;
        line-height: 30px;
        font-size: 30px;
      }
    }
  }
  .title-container.with-border {
    padding: 0 15px;
    border: 1px solid #b5b5b5;
  }
  .content {
    border-top: none;
  }
  .content.with-border {
    border: 1px solid #b5b5b5;
  }
  @keyframes easeIn {
    0% {
      opacity: 0;
      max-height: 0;
    }
    100% {
      opacity: 1;
      max-height: 600px;
    }
  }
  .mouse-pointer {
    cursor: pointer;
  }
</style>
